<script lang="ts">
  import LoadingSpinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import * as chartjs from "chart.js";
  import type Chart from "chart.js";
  import ChartComponent from "@shared/svelte/Components/Chart.svelte";
  import moment from "moment";
  import type { ReportDetailsMonth } from "@shared/types/Reports";

  export let purchaseDetailsByMonth: ReportDetailsMonth[] | null = null;
  export let layoutName = "desktop";

  const CHART_COLORS = Object.freeze([
    "#002B48",
    "#99AAB6",
    "#002B48",
    "#99AAB6",
    "#002B48",
    "#99AAB6",
    "#002B48",
    "#99AAB6",
    "#002B48",
    "#99AAB6",
    "#002B48",
    "#99AAB6",
  ]);

  let pluginTooltips = [];
  let readyForTooltips = false;

  // This plugin overrides the ChartJS default behavior (show one tooltip, on hover), with "show tooltips for all bars always" behavior
  // Because it depends on knowledge of some ChartJS internals it may break if ChartJS is updated.

  const plugin = {
    beforeRender: function (chart) {
      pluginTooltips = [];
      chart.config.data.datasets.forEach(function (dataset, i) {
        chart.getDatasetMeta(i).data.forEach(function (sector, j) {
          if (chart.data.datasets[0].data[j] != "0") {
            pluginTooltips.push(
              // @ts-ignore
              new chartjs.Chart.Tooltip(
                {
                  _chart: chart.chart,
                  _chartInstance: chart,
                  _data: chart.data,
                  _options: chart.options.tooltips,
                  _active: [sector],
                },
                chart
              )
            );
          }
        });

        chart.options.tooltips.enabled = false;
      });
    },
    afterDraw: function (chart, easing) {
      if (!readyForTooltips && easing !== 1) return;
      readyForTooltips = true;

      chart.options.tooltips.enabled = true;
      chartjs.Chart.helpers.each(pluginTooltips, function (tooltip) {
        tooltip.initialize();
        tooltip.update();

        tooltip.pivot();
        tooltip.transition(easing).draw();
      });
      chart.options.tooltips.enabled = false;
    },
  };

  const chartConfig: Chart.ChartConfiguration = {
    type: "bar",
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 50,
          bottom: 0,
        },
      },
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            display: true,
          },
        ],
        yAxes: [
          {
            display: false,
          },
        ],
      },
      tooltips: {
        callbacks: {
          label: function (tooltipItem, chartData) {
            if (selectedMeasurement === "spend") {
              return "$" + tooltipItem.yLabel;
            } else {
              return String(tooltipItem.yLabel);
            }
          },
          title: function (x, y) {
            return "";
          },
        },
        intersect: false,
        position: "average",
        caretSize: 0,
        cornerRadius: 6,

        // Typescript says these next two properties are not valid properties on the ChartTooltipOptions,
        // but these do affect the rendering position
        // @ts-ignore
        xAlign: "center",
        // @ts-ignore
        yAlign: "bottom",
      },
    },
    plugins: [plugin],
    data: {
      datasets: [],
      labels: [],
    },
  };

  type AvailableMeasurement = "quantity" | "spend" | "times_ordered";
  let selectedMeasurement: AvailableMeasurement = "quantity";

  const formatDollarString = number => "$" + parseFloat(number).toFixed(0).replace(/\.00$/, "");

  // Typescript doesn't like the root "moment()" function
  // @ts-ignore
  const dateToMonth = date => moment.localeData("en").monthsShort()[moment(date).month()];

  let chartData, min, max, lastSelectedMeasurement, lastPurchaseDetails;

  $: {
    if (
      purchaseDetailsByMonth &&
      (purchaseDetailsByMonth !== lastPurchaseDetails ||
        lastSelectedMeasurement !== selectedMeasurement)
    ) {
      // Sometimes or always, the API returns the current (13th) month with no or incomplete data, so we slice it out
      const validPurchaseDetails =
        purchaseDetailsByMonth.length === 13
          ? purchaseDetailsByMonth.slice(0, -1)
          : purchaseDetailsByMonth;

      const dataForDisplay = validPurchaseDetails.map(
        dataForMonth => dataForMonth[selectedMeasurement]
      );

      chartData = {
        datasets: [
          {
            backgroundColor: CHART_COLORS,
            data: dataForDisplay,
          },
        ],
        labels: validPurchaseDetails.map(dataForMonth => dateToMonth(dataForMonth.date)),
        series: [selectedMeasurement],
      };

      if (selectedMeasurement === "spend") {
        max = formatDollarString(Math.max(...dataForDisplay));
        min = formatDollarString(Math.min(...dataForDisplay));
      } else {
        max = Math.max(...dataForDisplay);
        min = Math.min(...dataForDisplay);
      }

      lastPurchaseDetails = purchaseDetailsByMonth;
      lastSelectedMeasurement = selectedMeasurement;
    }
  }

  $: loading = !purchaseDetailsByMonth;

</script>

<div class="fizz-styles">
  {#if loading}
    <LoadingSpinner />
  {:else if layoutName === "mobile"}
    <div class="margin-top--small">
      <div class="display-block uppercase secondary-small--bold">Previous 12 Months</div>
      <div class="margin-top--smaller margin-bottom--small">
        <ul class="list list--horizontal">
          <li
            class="list-item brand-body--regular cursor-pointer"
            class:brand-body--bold={selectedMeasurement === "quantity"}
            on:click={() => (selectedMeasurement = "quantity")}
          >
            Quantity
          </li>
          <li
            class="list-item brand-body--regular cursor-pointer"
            class:brand-body--bold={selectedMeasurement === "spend"}
            on:click={() => (selectedMeasurement = "spend")}
          >
            Spend
          </li>
          <li
            class="list-item brand-body--regular cursor-pointer"
            class:brand-body--bold={selectedMeasurement === "times_ordered"}
            on:click={() => (selectedMeasurement = "times_ordered")}
          >
            Times Ordered
          </li>
        </ul>
      </div>
      <div class="media">
        <div class="media-fluid">
          <div class="position-relative">
            <ChartComponent {chartConfig} {chartData} height="150" />
          </div>
        </div>
        <div class="media-fixed margin-left--smallest align-right">
          <div><span class="secondary-body--bold uppercase">High</span></div>
          <div>
            <span class="brand-body--bold" data-testid="chart-widget-max">{max}</span>
          </div>
          <div class="margin-top--smallest">
            <span class="secondary-body--bold uppercase">Low</span>
          </div>
          <div>
            <span class="brand-body--bold" data-testid="chart-widget-min">{min}</span>
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="media margin-top--medium">
      <div class="media-fixed">
        <div class="display-block uppercase secondary-small--bold">Previous 12 Months</div>
        <ul class="margin-top--small">
          <li
            class="brand-body--regular cursor-pointer"
            class:brand-body--bold={selectedMeasurement === "quantity"}
            on:click={() => (selectedMeasurement = "quantity")}
          >
            Quantity {#if selectedMeasurement === "quantity"} <span> > </span> {/if}
          </li>
          <li
            class="brand-body--regular cursor-pointer"
            class:brand-body--bold={selectedMeasurement === "spend"}
            on:click={() => (selectedMeasurement = "spend")}
          >
            Spend {#if selectedMeasurement === "spend"} <span> > </span> {/if}
          </li>
          <li
            class="brand-body--regular cursor-pointer"
            class:brand-body--bold={selectedMeasurement === "times_ordered"}
            on:click={() => (selectedMeasurement = "times_ordered")}
          >
            Times Ordered {#if selectedMeasurement === "times_ordered"} <span> > </span> {/if}
          </li>
        </ul>
      </div>
      <div class="media-fluid margin-right--medium margin-left--medium">
        <div class="position-relative full-width">
          <ChartComponent {chartConfig} {chartData} height="150" />
        </div>
      </div>
      <div class="media-fixed margin-right--small align-self-center">
        <div class="grid-row">
          <div class="grid-col c7-12"><span class="secondary-body--bold uppercase">High</span></div>
          <div class="grid-col c5-12"><span class="secondary-body--bold uppercase">Low</span></div>
        </div>
        <div class="grid-row">
          <div class="grid-col c7-12">
            <span class="brand-subheader--bold" data-testid="chart-widget-max">{max}</span>
          </div>
          <div class="grid-col c5-12">
            <span class="brand-subheader--bold" data-testid="chart-widget-min">{min}</span>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>
